import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Mail, ArrowRight, RefreshCw, Lock, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function MemberForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState("request"); // "request" | "confirm"
  const [email, setEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error("Email is required");
      return;
    }

    setLoading(true);
    try {
      await api.post("/api/members/auth/password-reset-request", { email });
      toast.success("Reset code sent! Check your email and backend logs.");
      setStep("confirm");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault();
    
    if (!resetCode.trim() || !newPassword.trim() || !confirmPassword.trim()) {
      toast.error("All fields are required");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    setLoading(true);
    try {
      await api.post("/api/members/auth/password-reset-confirm", {
        email,
        reset_code: resetCode,
        new_password: newPassword,
      });
      
      toast.success("Password reset successful! You can now login.");
      setTimeout(() => navigate("/members/login"), 2000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "#0a0a0a" }}>
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-6 relative">
            <div className="absolute inset-0 opacity-20 rounded-xl overflow-hidden" style={{ filter: "blur(12px)", transform: "scale(1.1)" }}>
              <div style={{
                background: "linear-gradient(135deg, #ec4899 0%, #a855f7 50%, #3b82f6 100%)",
                width: "100%",
                height: "100%",
                animation: "gradient-shift 6s ease infinite",
              }} />
            </div>
            <h1 className="relative text-3xl font-black px-4 py-2">
              <span style={{ color: "#ec4899" }}>GP</span>
              <span style={{ color: "#fff" }}> Studio</span>
            </h1>
          </Link>
          <h2 className="text-2xl font-bold mb-2" style={{ color: "#fff" }}>
            Reset Password
          </h2>
          <p className="text-sm" style={{ color: "#71717a" }}>
            {step === "request" 
              ? "Enter your email to receive a reset code" 
              : "Enter the reset code from your email"}
          </p>
        </div>

        {/* Step 1: Request Reset Code */}
        {step === "request" && (
          <form onSubmit={handleRequestReset} className="space-y-4 p-8 rounded-2xl border"
            style={{
              background: "rgba(24, 24, 27, 0.8)",
              backdropFilter: "blur(20px)",
              borderColor: "rgba(168, 85, 247, 0.2)",
            }}>
            
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: "#a1a1aa" }}>
                Email
              </label>
              <div className="relative">
                <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#71717a" }} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full pl-10 pr-4 py-3 rounded-lg border text-sm"
                  style={{
                    background: "rgba(0, 0, 0, 0.3)",
                    borderColor: "rgba(113, 113, 122, 0.3)",
                    color: "#fff",
                  }}
                  disabled={loading}
                  autoFocus
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg font-semibold text-sm inline-flex items-center justify-center gap-2 transition hover:scale-105 disabled:opacity-50"
              style={{
                background: "linear-gradient(135deg, #ec4899 0%, #a855f7 100%)",
                color: "#fff",
              }}>
              {loading ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  Send Reset Code
                  <ArrowRight size={16} />
                </>
              )}
            </button>
          </form>
        )}

        {/* Step 2: Confirm Reset */}
        {step === "confirm" && (
          <form onSubmit={handleConfirmReset} className="space-y-4 p-8 rounded-2xl border"
            style={{
              background: "rgba(24, 24, 27, 0.8)",
              backdropFilter: "blur(20px)",
              borderColor: "rgba(168, 85, 247, 0.2)",
            }}>
            
            {/* Reset Code */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: "#a1a1aa" }}>
                6-Digit Reset Code
              </label>
              <div className="relative">
                <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#71717a" }} />
                <input
                  type="text"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  className="w-full pl-10 pr-4 py-3 rounded-lg border text-sm tracking-widest text-center text-2xl"
                  style={{
                    background: "rgba(0, 0, 0, 0.3)",
                    borderColor: "rgba(113, 113, 122, 0.3)",
                    color: "#fff",
                  }}
                  disabled={loading}
                  autoFocus
                  maxLength={6}
                />
              </div>
              <p className="text-xs mt-2" style={{ color: "#71717a" }}>
                Check your email or backend logs for the code
              </p>
            </div>

            {/* New Password */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: "#a1a1aa" }}>
                New Password
              </label>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#71717a" }} />
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-10 pr-4 py-3 rounded-lg border text-sm"
                  style={{
                    background: "rgba(0, 0, 0, 0.3)",
                    borderColor: "rgba(113, 113, 122, 0.3)",
                    color: "#fff",
                  }}
                  disabled={loading}
                />
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: "#a1a1aa" }}>
                Confirm Password
              </label>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#71717a" }} />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-10 pr-4 py-3 rounded-lg border text-sm"
                  style={{
                    background: "rgba(0, 0, 0, 0.3)",
                    borderColor: "rgba(113, 113, 122, 0.3)",
                    color: "#fff",
                  }}
                  disabled={loading}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg font-semibold text-sm inline-flex items-center justify-center gap-2 transition hover:scale-105 disabled:opacity-50"
              style={{
                background: "linear-gradient(135deg, #ec4899 0%, #a855f7 100%)",
                color: "#fff",
              }}>
              {loading ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  Resetting...
                </>
              ) : (
                <>
                  Reset Password
                  <CheckCircle size={16} />
                </>
              )}
            </button>

            {/* Back to Request */}
            <button
              type="button"
              onClick={() => setStep("request")}
              className="w-full text-sm hover:underline"
              style={{ color: "#71717a" }}>
              ← Request a new code
            </button>
          </form>
        )}

        {/* Back to Login */}
        <div className="mt-6 text-center">
          <Link to="/members/login" className="text-sm hover:underline" style={{ color: "#71717a" }}>
            ← Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
}
